import { Factory } from '../../services/Factory';
import { INotificationService } from '../../services/notification';
import { SearchTermListService } from '../../services/terms/SearchTermListService';
import { SearchTermService } from '../../services/terms/SearchTermService';
import { SearchTermJobService } from '../../services/terms/SearchTermJobService';
import { IRootScope } from '../../IRootScope';
import { SearchTermList } from '../../model/terms/SearchTermList';
import { SearchTerm } from '../../model/terms/SearchTerm';

interface ISearchTermControllerScope extends angular.IScope {
    searchTermPageMode: string,
    project: any;
    searchTermLists: Array<any>;
    searchTermList: any,
    searchTermJobsPage: any,
    searchTermsPage: any,
    saving: boolean;
    openSearchTermList: Function,
    closeSearchTermList: Function,
    runSearchTermJob: Function,
    cancelSearchTermJob: Function,
    deleteSearchTerm: Function,
    saveNewSearchTerm: Function,
    updateSearchTerm: Function,
    startSearchTermEdit: Function,
    saveSearchTermEdit: Function,
    cancelSearchTermEdit: Function,
    openAddSearchTermListDialog: Function,
    searchForTerm: Function,
    searchForAllTerms: Function,
    newSearchTerm: any,
    allTermsValid: boolean
  }

export class SearchTermController {
  private $scope: ISearchTermControllerScope;
  private $rootScope: IRootScope;
  private $timeout;
  private notificationService: INotificationService;
  private searchTermListService: SearchTermListService;
  private searchTermService: SearchTermService;
  private searchTermJobService: SearchTermJobService;

  constructor($scope: ISearchTermControllerScope, $rootScope: IRootScope, $timeout, $uibModal, notificationService: INotificationService, searchTermListService: SearchTermListService, searchTermService: SearchTermService, searchTermJobService: SearchTermJobService) {
    this.$scope = $scope;
    this.$rootScope = $rootScope;
    this.$timeout = $timeout;
    this.notificationService = notificationService;
    this.searchTermListService = searchTermListService;
    this.searchTermService = searchTermService;
    this.searchTermJobService = searchTermJobService;

    $scope.searchTermPageMode = "list";
    $scope.running = true;

      $scope.$watch("project.id", (val: string) => {
        if (val) {
          this.refreshSearchTermLists();
        }
      });

      $scope.openAddSearchTermListDialog = () => {
        const searchTermListModalInstance = $uibModal.open({
          backdrop: "static",
          keyboard: true,
          backdropClick: false,
          templateUrl: "partials/terms/dialog_add_search_term_list.html",
          controller: "AddSearchTermListModalController",
          scope: $scope,
          resolve: {
            project: () => $scope.project,
            searchTermLists: () => $scope.searchTermLists,
            message: () => ""
          }
        });

        searchTermListModalInstance.result.then((newSearchTermList) => {
          if (newSearchTermList) {
            $scope.openSearchTermList(newSearchTermList);
            this.refreshSearchTermLists();
          }
        });
      };

      $scope.openSearchTermList = (searchTermList: any) => {
        $scope.searchTermList = searchTermList;
        $scope.newSearchTerm = { projectId: $scope.project.id, searchTermListId: $scope.searchTermList.id, term: "" };

        this.refreshSearchTerms();
        this.refreshSearchTermJobs();
      };

      $scope.closeSearchTermList = () => {
        $scope.searchTermList = null;
      };

      $scope.$watch('searchTermsPage.searchTerms', function (terms: any[]) {
        $scope.allTermsValid = terms && terms.every(term => term.isValid);
      }, true); // deep watch

      $scope.deleteSearchTerm = searchTerm => searchTermService.Delete(searchTerm, () => {
        this.notificationService.addSuccess({
          title: "Search term deleted",
          description: "The search term was deleted successfully",
          timeout: 5000
        });
        this.refreshSearchTerms();
      });

      $scope.saveNewSearchTerm = newSearchTerm => {
        this.$scope.saving = true;
        searchTermService.Save(newSearchTerm).then(() => {
          this.$scope.saving = false;
          this.$scope.newSearchTerm.term = "";
          this.notificationService.addSuccess({
            title: "Search term added",
            description: "The search term was added successfully",
            timeout: 5000
          });
          this.refreshSearchTerms();
        }, () => {
          this.$scope.saving = false;
          this.notificationService.addError({
            title: "Error adding search term",
            description: "That's embarrassing, please try again later",
            timeout: 5000
          });
          this.refreshSearchTerms();
        });
      }

      $scope.updateSearchTerm = searchTerm => {
        this.$scope.saving = true;
        this.searchTermService.Update(searchTerm).then(() => {
          this.$scope.saving = false;
          this.notificationService.addSuccess({
            title: "Search term updated",
            description: "The search term was updated successfully",
            timeout: 5000
          });
          this.refreshSearchTerms();
        }, () => {
          this.$scope.saving = false;
          this.notificationService.addError({
            title: "Error updating search term",
            description: "That's embarrassing, please try again later",
            timeout: 5000
          });
        });
      }

      $scope.startSearchTermEdit = function (term) {
        if (!term._editing) {
          term._editing = true;
          term._editedTerm = term.term;
        }
      };

      $scope.saveSearchTermEdit = function (term) {
        term.term = term._editedTerm;
        term._editing = false;
        $scope.updateSearchTerm(term);
      };

      $scope.cancelSearchTermEdit = function (term) {
        term._editing = false;
      };

      $scope.runSearchTermJob = (searchTermList: SearchTermList) => {
        this.$scope.saving = true;
        this.$scope.running = true;
        this.$scope.cancelling = false;

        this.searchTermJobService.Run(searchTermList.projectId, searchTermList.id).then(() => {
          this.notificationService.addSuccess({
            title: "Search term list started",
            description: "The search term list was started successfully",
            timeout: 5000
          });
          this.refreshSearchTermJobs();
        }, () => {
          this.notificationService.addError({
            title: "Error starting search term list",
            description: "That's embarrassing, please try again later",
            timeout: 5000
          });
        }).finally(() => {
          this.$scope.saving = false;
        });
      };

      $scope.cancelSearchTermJob = (searchTermList: SearchTermList) => {
        $scope.cancelling = true;
        this.searchTermJobService.Cancel(searchTermList.projectId, searchTermList.id).then(null, () => {
          this.notificationService.addError({
            title: "Error cancelling search term job",
            description: "That's embarrassing, please try again later",
            timeout: 5000
          });
        });
      };

      $scope.searchForTerm = (term: SearchTerm) => {
        $rootScope.$broadcast("BasicSearch.Triggered", `searchTags:"${term.tag.id}" AND NOT systemTags:"${this.$scope.searchTermList.duplicateTag.id}"`);
      }

      $scope.searchForAllTerms = () => {
        $rootScope.$broadcast("BasicSearch.Triggered", `searchTags:"${this.$scope.searchTermList.tag.id}" AND NOT systemTags:"${this.$scope.searchTermList.duplicateTag.id}"`);
      }

      $scope.$on("FeedService.SearchTermComplete", async (e: angular.IAngularEvent, message) => {
        this.notificationService.addSuccess({
          title: `Search Terms Job ${message.job.status}`,
          description: `${message.description} has completed.`
        });

        $scope.cancelling = false;
        $scope.running = false;

        this.refreshSearchTermJobs();
        this.refreshSearchTermLists();
      });
    }

  private refreshSearchTermLists() {
    this.searchTermListService.FindAll(this.$scope.project.id).then(searchTermListPage => {
      this.$timeout(() => {
        this.$scope.searchTermLists = searchTermListPage.searchTermLists
        this.$scope.searchTermList = _.where(searchTermListPage.searchTermLists, { id: this.$scope.searchTermList?.id })[0];
      });
    });
  }

  private refreshSearchTermJobs() {
    this.searchTermJobService.FindAll(this.$scope.project.id, this.$scope.searchTermList.id).then((searchTermJobs) => {
      this.$timeout(() => {
        let jobs = [...searchTermJobs.searchTermJobs];
        this.$scope.searchTermJobsPage = searchTermJobs;
        this.$scope.running = _.find(jobs, job => job.status === "Running") instanceof Object;
      });
    });
  }

  private refreshSearchTerms() {
    this.searchTermService.FindAll({
      projectId: this.$scope.project.id,
      searchTermListId: this.$scope.searchTermList.id
    } as ISearchTermParams).then((searchTerms) => {
      this.$timeout(() => {
        this.$scope.searchTermsPage = searchTerms;
      });
    });
  }
}

frontendApp.controller("SearchTermController", ["$scope", "$rootScope", "$timeout", "$uibModal", "NotificationService", "SearchTermListService", "SearchTermService", "SearchTermJobService", Factory.Create(SearchTermController)]);
